import moment from 'moment';
import Mustache from 'mustache';

import { EVENT_CARD_FIXED_WIDTH } from 'vd-constants';

import * as EventUtil from 'utils/EventUtil';
import {
  BaseFieldRenderer,
  type FieldRendererProps,
  type RenderContentStaticOptions,
} from './base';
import { PrintReceiptBlock } from './printRows';
import { RendererUtil } from './RendererUtil';
import { type FieldRendererField } from './types';

import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type IEvent } from 'clients/PlatformApi';
import memoizeOne from 'memoize-one';
import { getReduxStore } from 'stores/store';

interface I18nContext {
  [key: string]: {
    fullName: string;
    shortName: string;
    values: { [key: string]: string } | undefined;
  };
}

type Order = NonNullable<NonNullable<NonNullable<IEvent['transaction']>['orders']>[0]>;
type OrderItem = NonNullable<Order['items']>[0];

type QueueItem = NonNullable<NonNullable<IEvent['queue']>['items']>[0];
type RemappedQueueItem = Omit<QueueItem, 'duration' | 'time' | 'type'> & {
  duration: string | undefined;
  isExit: boolean;
  time: number | undefined;
  type: string | undefined;
};

/**
 * Use DEBUG_MODE to;
 * * Make render context available as component state for easy inspection with React dev tools
 * * Remove time and currency formatting
 * * Add side-by-side comparison of new vs old receipt (scroll right in the event details)
 * * Add italic font styling to receipt item that differ
 * * Add as title to the rendered receipt line the expected receipt line (from `details.Receipt`)
 */
const DEBUG_MODE = false;
const FIXED_WIDTH = EVENT_CARD_FIXED_WIDTH;
const A_CONTENT_RE = /(<a[^>]*>)(.+?)(<\/a>)/;
const A_PRE_CONTENT_RE = /(<a[^>]*>)(.+?)$/;
const A_POST_CONTENT_RE = /^(.+?)(<\/a>)/;
const DRIVE_THRU_ORDER_TYPE = 'Drive-Thru';
const CURRENCY = '$'; // should we make this customizable?

const DEFAULT_TRANSACTION_TEMPLATE =
  '\
{{{transactionHeader}}}\
{{{transactionStoredOrders}}}\
{{{queueItems}}}\
{{{transactionFinalOrder}}}\
{{{transactionFooter}}}';

const TRANSACTION_HEADER_TEMPLATE =
  '\
{{#hasQueue}}\
{{i18n.queue_storeNumber.shortName}}: {{queue.storeNumber}}\t{{startTime}}\n\
{{i18n.queue_queueNumber.shortName}}: {{queue.queueNumber}}\t{{i18n.queue_queueCount.fullName}}: {{queue.queueCount}}\n\
\n\
{{/hasQueue}}';

const TRANSACTION_ORDER_ITEM_TEMPLATE =
  '\
{{#isSale}}\
<a class="item-link" data-time="{{time}}">{{quantity}}{{description}}\t{{unitPrice}} {{extendedPrice}}\
{{/isSale}}\
{{#isReplace}}\
<a class="item-link" data-time="{{time}}">   {{description}}\t\
{{/isReplace}}\
{{#isQualifier}}\
<a class="item-link" data-time="{{time}}">   {{quantity}} {{description}}\t\
{{/isQualifier}}\
{{#isVoidQualifier}}\
<a class="item-link" data-time="{{time}}">   {{description}}\t\
{{/isVoidQualifier}}\
{{#isOrderStored}}\
\n\n<a class="item-link" data-time="{{time}}">{{type}} {{description}}\t\
{{/isOrderStored}}\
{{#isTax}}\
<a class="item-link" data-time="{{time}}">{{#chargesAlign}}{{description}}{{/chargesAlign}}:\t{{unitPrice}}\
{{/isTax}}\
{{#isPayment}}\
<a class="item-link" data-time="{{time}}">{{#chargesAlign}}{{type}} {{description}}{{/chargesAlign}}:\t{{unitPrice}}\
{{/isPayment}}\
{{#isOther}}\
<a class="item-link" data-time="{{time}}">   {{description}}\t\
{{/isOther}}\
{{#hasDescriptionRemainder}}\
{{#descriptionRemainder}}\
\n\
{{#isSale}}\
   \
{{/isSale}}\
{{#isReplace}}\
   \
{{/isReplace}}\
{{#isQualifier}}\
   \
{{/isQualifier}}\
{{#isTax}}\
             \
{{/isTax}}\
{{#isPayment}}\
             \
{{/isPayment}}\
{{line}}\t\
{{/descriptionRemainder}}\
{{/hasDescriptionRemainder}}\
</a>';

const TRANSACTION_STORED_ORDERS_TEMPLATE =
  '\
{{#hasTransaction}}\
{{#transaction.orders}}\
\n\n{{orderStatus}}\n\
{{i18n.transaction_orders_receiptNumber.shortName}}: {{receiptNumber}}\t{{time}}\n\
{{i18n.transaction_orders_employeeName.shortName}}: {{employeeName}}\t{{#showOrderType}}{{i18n.transaction_orders_orderType.shortName}}: {{orderType}}{{/showOrderType}}\n\
\n\
{{#soldItems}}\
' +
  TRANSACTION_ORDER_ITEM_TEMPLATE +
  '\n\
{{/soldItems}}\
\n\
\n{{lineSeparator}}\n\
{{/transaction.orders}}\
{{/hasTransaction}}';

const QUEUE_ITEMS_TEMPLATE =
  '\
{{#hasQueue}}\
\n\n<a class="item-link" data-time="{{queue.detectItem.time}}">   {{queue.detectItem.type}}\t{{queue.detectItem.duration}}</a>\n\
{{#queue.greetOrOrderItems}}\
<a class="item-link" data-time="{{time}}">    {{type}}\t{{duration}}         </a>\n\
{{/queue.greetOrOrderItems}}\
{{#queue.otherItems}}\
{{^isExit}}\
\n\n<a class="item-link" data-time="{{time}}">   {{type}}\t{{duration}}</a>\n\
{{/isExit}}\
{{/queue.otherItems}}\
\n\n   {{i18n.queue_totalQueueTime.fullName}}\t{{queue.totalQueueTime}}\
\n\n\
{{lineSeparator}}\n\
{{/hasQueue}}\
{{^hasQueue}}\
{{#missingDTTimerData}}\
 \n\n\n\n\
***  NO DRIVE THRU TIMER DATA FOUND   ***\n\
 \n\
 \n\
 \n\
{{lineSeparator}}\n\
{{/missingDTTimerData}}\
\
{{/hasQueue}}';

const TRANSACTION_FINAL_ORDER_TEMPLATE =
  '\
{{#hasTransaction}}\
\n\n{{transaction.finalOrder.orderStatus}}\n\
{{i18n.transaction_orders_receiptNumber.shortName}}: {{transaction.finalOrder.receiptNumber}}\t{{transaction.finalOrder.time}}\n\
{{i18n.transaction_orders_employeeName.shortName}}: {{transaction.finalOrder.employeeName}}\t{{#transaction.finalOrder.showOrderType}}{{i18n.transaction_orders_orderType.shortName}}: {{transaction.finalOrder.orderType}}{{/transaction.finalOrder.showOrderType}}\n\
\n\
{{#transaction.finalOrder.soldItems}}\
' +
  TRANSACTION_ORDER_ITEM_TEMPLATE +
  '\n\
{{/transaction.finalOrder.soldItems}}\
\n\n\n\
{{#chargesAlign}}{{i18n.transaction_transactionId.shortName}}{{/chargesAlign}}:\t{{transaction.transactionId}}\n\
{{#transaction.hasSubtotal}}\
{{#chargesAlign}}{{i18n.transaction_subtotal.shortName}}{{/chargesAlign}}:\t{{transaction.subtotal}}\n\
{{/transaction.hasSubtotal}}\
{{#transaction.finalOrder.taxItems}}\
' +
  TRANSACTION_ORDER_ITEM_TEMPLATE +
  '\n\
{{/transaction.finalOrder.taxItems}}\
{{#chargesAlign}}{{i18n.transaction_total.shortName}}{{/chargesAlign}}:\t{{transaction.total}}\n\
{{#transaction.finalOrder.paymentItems}}\
' +
  TRANSACTION_ORDER_ITEM_TEMPLATE +
  '\n\
{{/transaction.finalOrder.paymentItems}}\
{{#transaction.hasChangeDue}}\
{{#chargesAlign}}{{i18n.transaction_changeDue.shortName}}{{/chargesAlign}}:\t{{transaction.changeDue}}\n\
{{/transaction.hasChangeDue}}\
\n\n{{lineSeparator}}\n\
\n\
{{/hasTransaction}}';

const TRANSACTION_FOOTER_TEMPLATE =
  '\
{{#hasQueue}}\
{{i18n.queue_departureTime.shortName}}:\t{{queue.departureTime}}\
{{/hasQueue}}';

const localeDate = (ts: Date | number | undefined, timezoneId?: string) => {
  if (ts === undefined) {
    return undefined;
  }

  const localeCode = getReduxStore().getState().localeState.localeCode;
  const tsd = new Date(ts);
  if (DEBUG_MODE) {
    return moment(ts).format('M/D/YYYY h:mm:ss A');
  } else if (timezoneId) {
    return moment(ts).tz(timezoneId).format('M/D/YYYY h:mm:ss A');
  }
  return `${tsd.toLocaleDateString(localeCode)} ${tsd
    .toLocaleTimeString(localeCode)
    .toUpperCase()
    .replace('A.M.', 'AM')
    .replace('P.M.', 'PM')}`;
};

// This is a "sample date" used when estimating the height of each transaction.
const getSampleDate = () => localeDate(new Date('2020-12-31T17:00:00.000Z'));

const monetaryValue = (val: number | undefined, noCurrency?: boolean) => {
  const MAX_WIDTH = 8;
  if (val !== 0 && !val) {
    return val;
  }
  if (noCurrency) {
    return val.toFixed(2).padStart(MAX_WIDTH);
  }
  const result = val >= 0 ? `${CURRENCY} ${val.toFixed(2)}` : `${CURRENCY}${val.toFixed(2)}`;
  return result.padStart(MAX_WIDTH);
};

const timestamp = (date: Date | number | undefined) => {
  if (date === undefined) {
    return undefined;
  }
  return moment(date).valueOf();
};

const capitalize = (val: string) => {
  if (!val) {
    return val;
  }
  const ucFirst = (string: string) => string.slice(0, 1).toUpperCase() + string.slice(1);
  return [' ', '(', '['].reduce((acc, curr) => {
    return acc.split(curr).map(ucFirst).join(curr);
  }, val);
};

const timeFormat = (duration: number | undefined) => {
  if (!duration) {
    return undefined;
  }
  return RendererUtil.timeFormat(duration, DEBUG_MODE);
};

const isIterable = (obj: any) => {
  // checks for null and undefined
  if (obj == null) {
    return false;
  }
  return typeof obj[Symbol.iterator] === 'function';
};

const MAX_ORDER_ITEM_DESCRIPTION_WIDTH: { [key in NonNullable<OrderItem['type']>]?: number } = {
  sale: 21,
  void: 21,
  replace: 21,
  qualifier: 19,
  payment: 25,
  tax: 25,
};

/**
 * Renders the `transaction` and/or `queue` field of an event.
 * This effectively replicates the receipt that QST parser is producing,
 * enhancing the format a fair bit, adding links to receipt item video,
 * and enabling i18n for a while bunch of field names.
 */
export class TransactionQueueFieldRenderer extends BaseFieldRenderer {
  constructor(props: FieldRendererProps) {
    super(props);

    // useful for debugging receipt rendering
    if (DEBUG_MODE) {
      this.state = makeSubTemplateContext(props.eventStandard, props.event);
    }
  }

  static getTemplate(fieldMetadata: FieldRendererField) {
    return fieldMetadata.template || DEFAULT_TRANSACTION_TEMPLATE;
  }

  onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.props.onContextMenu(event);
  };

  componentDidMount() {
    this.node?.querySelectorAll('a.item-link').forEach((item) => {
      item.addEventListener('click', this.viewItemInVideo);
    });
  }

  viewItemInVideo = (clickEvent: Event) => {
    const { event, onEventRowClick } = this.props;
    if (clickEvent.target) {
      const el = clickEvent.target as HTMLAnchorElement;
      const itemTime = el.attributes.getNamedItem('data-time')?.value;
      if (itemTime && itemTime.length) {
        const cameraId = event.primaryCameraId || event.cameras[0]?.id;
        onEventRowClick(event, cameraId, parseInt(itemTime, 10));
      }
    }
  };

  render() {
    const { event, fieldName } = this.props;
    // this renderer is applicable to both `event.queue` and `event.transaction`,
    // however each instance will intertwine the data from both fields in the render output.
    // if the event has both, one of them has to be muted to avoid duplicate
    if (fieldName === 'queue' && !!event.queue && !!event.transaction) {
      return null;
    }
    if (!['queue', 'transaction'].includes(fieldName)) {
      return null;
    }

    if (this.props.variant === 'print') {
      return (
        <div onContextMenu={this.onContextMenu}>
          <PrintReceiptBlock html={this.renderContent()} />
        </div>
      );
    }

    return super.render();
  }

  renderContent() {
    const { eventStandard, event, fieldMetadata, timezone } = this.props;

    const renderedLines = this.getStaticRenderer().renderContentStatic(
      eventStandard,
      fieldMetadata,
      event,
      { timezone },
    );

    if (DEBUG_MODE) {
      // side-by-side receipt comparison useful for receipt easy comparison.
      const receiptLines = (event.details?.Receipt || '').split('\n');
      return `<div class="pre">${renderedLines
        .map((l, idx) => {
          if (idx < receiptLines.length) {
            const textContent = l.replace(A_CONTENT_RE, '$2');
            let newL = l;
            const div = document.createElement('div');
            div.innerHTML = textContent.trim();
            if (receiptLines[idx].trim() !== div.innerText.trim()) {
              console.log(receiptLines[idx].trim(), '!==', div.innerText.trim());
              newL = `<span style="font-style: italic; text-decoration: underline dotted red !important;" title="${receiptLines[idx]}">${l}</span>`;
            }
            return newL + '    |' + receiptLines[idx].padEnd(FIXED_WIDTH) + '|  ';
          }
          return l;
        })
        .join('</div><div class="pre">')}</div>`;
    }

    return `<div class="pre">${renderedLines.join('</div><div class="pre">')}</div>`;
  }

  /**
   * Applies the fixed width algorithm to the provided content.
   * The algorithm detects presence of `\t` dynamic space markers, considers the amount of text
   * on each line on both sides of the marker, compute the amount of space required to make each
   * line fixed width and inserts them in place of the markers.
   * If the line is too long, it will be broken down.
   *
   * @param renderedContent - Mustache output. HTML tags and entities will be ignored.
   * @return fixed width content, one line per entry in the array.
   */
  static makeFixedWidth(renderedContent: string): string[] {
    // This function is used to strip the <a> tag, extract the text content,
    // calls a processor function (`makeLineFixedWidth`) then add back the stripped <a> tag
    // to each returned line.
    const withStrippedATag = (processorFn: (str: string) => string[]) => {
      return (renderedLine: string) => {
        // case for: <a attr="val" ...>textContent</a>
        let match = A_CONTENT_RE.exec(renderedLine);
        if (match && match.length > 3) {
          const contentMatch = match;
          const lines = processorFn(contentMatch[2]);
          // add surrounding stripped <a> tag to each line
          return lines.map((line) => `${contentMatch[1]}${line}${contentMatch[3]}`);
        }

        // case for <a attr="val" ...>textContent
        match = A_PRE_CONTENT_RE.exec(renderedLine);
        if (match && match.length > 2) {
          const preContentMatch = match;
          const lines = processorFn(preContentMatch[2]);
          // add stripped <a> tag back in front of the first line
          return lines.map((line, idx) => (idx === 0 ? `${preContentMatch[1]}${line}` : line));
        }

        // case for textContent</a>
        match = A_POST_CONTENT_RE.exec(renderedLine);
        if (match && match.length > 2) {
          const postContentMatch = match;
          const lines = processorFn(postContentMatch[1]);
          // add stripped <a> tag back at the end of the last line
          return lines.map((line, idx) =>
            idx === lines.length - 1 ? `${line}${postContentMatch[2]}` : line,
          );
        }

        return processorFn(renderedLine);
      };
    };

    // apply the fixed width algorithm to the text part of each line of the rendered content
    const makeLineFixedWidth = RendererUtil.getLineFixedWidthMaker(FIXED_WIDTH);
    const fixedWidthLines = renderedContent.split('\n').map(withStrippedATag(makeLineFixedWidth));
    return fixedWidthLines.flat();
  }

  // makeTemplateContext() {
  //   const { eventStandard, event, timezone, locale } = this.props;

  //   return TransactionQueueFieldRenderer.makeTemplateContextStatic(eventStandard, event, {
  //     timezone,
  //     locale,
  //   });
  // }

  /**
   * @param [options.heightOnly=false] - Passed when we're trying to estimate the
   * height of this object - we don't need to accurately render every detail
   * in this case, we just need to render fast.
   */
  static renderContentStatic(
    eventStandard: EventStandardDefs,
    fieldMetadata: FieldRendererField,
    event: IEvent,
    options?: RenderContentStaticOptions,
  ) {
    const rendered = Mustache.render(
      fieldMetadata.template || DEFAULT_TRANSACTION_TEMPLATE,
      makeTemplateContext(eventStandard, event, options),
    );

    // Would be nice to not call `makeFixedWidth()` here in the `heightOnly: true`
    // case, but sometimes lines will be split at a "\t" marker, so we need to.
    return this.makeFixedWidth(rendered);
  }
}

function makeTemplateContext(
  eventStandard: EventStandardDefs,
  event: IEvent,
  options?: { timezone?: string },
): {
  queueItems: string;
  transactionFinalOrder: string;
  transactionFooter: string;
  transactionHeader: string;
  transactionStoredOrders: string;
} {
  const context = makeSubTemplateContext(eventStandard, event, options);

  return {
    queueItems: Mustache.render(QUEUE_ITEMS_TEMPLATE, context),
    transactionFinalOrder: Mustache.render(TRANSACTION_FINAL_ORDER_TEMPLATE, context),
    transactionFooter: Mustache.render(TRANSACTION_FOOTER_TEMPLATE, context),
    transactionHeader: Mustache.render(TRANSACTION_HEADER_TEMPLATE, context),
    transactionStoredOrders: Mustache.render(TRANSACTION_STORED_ORDERS_TEMPLATE, context),
  };
}

function makeSubTemplateContext(
  eventStandard: EventStandardDefs,
  event: IEvent,
  options?: { heightOnly?: boolean; timezone?: string },
) {
  const i18n = makeI18nContext(eventStandard);
  const heightOnly = options?.heightOnly ?? false;

  return {
    chargesAlign: () => (text: string, render: (t: string) => string) => {
      const rendered = render(text);
      return rendered.padStart(27);
    },
    hasQueue: !!event.queue,
    hasTransaction: !!event.transaction,
    i18n,
    lineSeparator: new Array(FIXED_WIDTH).fill('-').join(''),
    missingDTTimerData: hasMissingDTTimerData(event),
    queue: event.queue && remapQueue(event.queue, i18n, options),
    startTime: heightOnly ? getSampleDate() : localeDate(event.startTime, options?.timezone),
    transaction: event.transaction && remapTransaction(event.transaction, i18n, options),
  };
}

function hasMissingDTTimerData({ transaction, queue }: IEvent): boolean {
  const isDT =
    transaction &&
    transaction.orders &&
    transaction.orders.some((o) => o.orderType === DRIVE_THRU_ORDER_TYPE);
  const hasMissingTimer = !queue?.items?.length;
  return !!isDT && hasMissingTimer;
}

const makeI18nContext = memoizeOne((eventStandard: EventStandardDefs): I18nContext => {
  const i18n: I18nContext = {};

  for (const key of Object.keys(eventStandard)) {
    if (key.indexOf('queue') !== 0 && key.indexOf('transaction') !== 0) {
      continue;
    }

    /**
     * The `key` is something like `transaction.orders.receiptNumber`.
     * We want to convert it to `transaction_orders_receiptNumber` for the i18n key.
     * Otherwise, we'd have the i18n object looking like this:
     * ```
     * {
     *  "transaction.orders.receiptNumber": {
     *   fullName: "Receipt Number",
     * }
     * ```
     * But for Mustache template {{i18n.transaction.orders.receiptNumber}}, it will look for nested object like this:
     * ```
     * {
     *  "transaction": {
     *    "orders": {
     *      "receiptNumber": {
     *          fullName: "Receipt Number",
     *     }
     *   }
     * }
     * ```
     */
    i18n[key.replaceAll('.', '_')] = {
      fullName: EventUtil.i18nEventFieldName(key, true, eventStandard),
      shortName: EventUtil.i18nEventFieldName(key, false, eventStandard),
      values: eventStandard[key].values
        ? EventUtil.i18nEventFieldEnumValues(key, eventStandard)
        : undefined,
    };
  }

  return i18n;
});

function remapTransaction(
  transaction: NonNullable<IEvent['transaction']>,
  i18n: I18nContext,
  options?: { heightOnly?: boolean; timezone?: string },
) {
  return {
    ...transaction,
    changeDue: monetaryValue(transaction.changeDue),
    finalOrder:
      transaction.orders &&
      transaction.orders.length > 0 &&
      remapOrderObject(
        transaction.orders.slice(-1)[0],
        i18n,
        transaction.orders.length === 1,
        options,
      ),
    hasChangeDue: transaction.changeDue && transaction.changeDue > 0,
    hasSubtotal:
      transaction.subtotal &&
      transaction.subtotal > 0 &&
      transaction.subtotal !== transaction.total,
    orders: transaction.orders && remapOrdersArray(transaction.orders, i18n, options),
    subtotal: monetaryValue(transaction.subtotal),
    total: monetaryValue(transaction.total),
  };
}

function remapOrdersArray(
  transactionOrders: readonly Order[],
  i18n: I18nContext,
  options?: { heightOnly?: boolean; timezone?: string },
) {
  return transactionOrders
    .slice(0, -1)
    .map((order, idx) => remapOrderObject(order, i18n, idx === 0, options));
}

function remapOrderObject(
  order: Order,
  i18n: I18nContext,
  isFirstOrder: boolean,
  options?: { heightOnly?: boolean; timezone?: string },
) {
  return {
    ...order,
    orderStatus: order.orderStatus
      ? i18n['transaction_orders_orderStatus']?.values?.[order.orderStatus]
      : undefined,
    showOrderType: order.orderType && isFirstOrder,
    time: options?.heightOnly ? getSampleDate() : localeDate(order.time, options?.timezone),
    ...separateOrderItems(order.items, i18n),
  };
}

function separateOrderItems(orderItems: Order['items'], i18n: I18nContext) {
  const soldItems: any[] = [];
  const taxItems: any[] = [];
  const paymentItems: any[] = [];

  if (!orderItems || !isIterable(orderItems)) {
    return;
  }

  for (const item of orderItems) {
    if (!item.type || !item.description) {
      continue;
    }
    if (item.type === 'tax') {
      taxItems.push(remapOrderItem(item, i18n));
    } else if (item.type === 'payment') {
      paymentItems.push(remapOrderItem(item, i18n));
    } else {
      soldItems.push(remapOrderItem(item, i18n));
    }
  }
  return { paymentItems, soldItems, taxItems };
}

function remapOrderItem(item: OrderItem, i18n: I18nContext) {
  const itemType = item.type ?? '';

  const { descriptionHeader, descriptionRemainder } = splitDescriptionLines(
    item.description,
    MAX_ORDER_ITEM_DESCRIPTION_WIDTH[itemType] || 23,
  );

  return {
    ...item,
    description: ['payment', 'tax'].includes(itemType)
      ? descriptionHeader.toUpperCase()
      : capitalize(descriptionHeader),
    descriptionRemainder,
    extendedPrice: monetaryValue(
      item.extendedPrice,
      DEBUG_MODE && !['payment', 'tax'].includes(itemType),
    ),
    hasDescriptionRemainder: descriptionRemainder.length > 0,
    isOrderStored: itemType === 'orderStored',
    isPayment: itemType === 'payment',
    isQualifier: itemType === 'qualifier',
    isVoidQualifier: itemType === 'void-qualifier',
    isReplace: itemType === 'replace',
    isSale: ['sale', 'void', 'tims-rewards', 'discount', 'refund'].includes(itemType),
    isTax: itemType === 'tax',
    isOther: ![
      'discount',
      'orderStored',
      'payment',
      'qualifier',
      'refund',
      'replace',
      'sale',
      'tims-rewards',
      'tax',
      'void',
      'void-qualifier',
    ].includes(itemType),
    quantity:
      item.quantity &&
      (['qualifier', 'replace'].includes(itemType)
        ? item.quantity.toString()
        : (item.quantity || 0).toString().padEnd(3)),
    time: timestamp(item.time),
    type: i18n['transaction_orders_items_type'].values?.[itemType],
    unitPrice: monetaryValue(item.unitPrice, DEBUG_MODE && !['payment', 'tax'].includes(itemType)),
  };
}

function splitDescriptionLines(
  description: string | undefined,
  maxLineLen: number,
): {
  descriptionHeader: string;
  descriptionRemainder: { line: string }[];
} {
  const lines: string[][] = [[]];
  const tokens = (description || '').split(' ');
  let lineNum = 0;
  while (tokens.length > 0) {
    const token = tokens.shift();
    if (token) {
      const lineLength = lines[lineNum].reduce((acc, token) => acc + token.length + 1, 0);
      if (lineLength === 0 || lineLength + token.length < maxLineLen) {
        lines[lineNum].push(token);
      } else {
        lines.push([token]);
        lineNum++;
      }
    }
  }

  return {
    descriptionHeader: capitalize(lines[0].join(' ')),
    descriptionRemainder: lines.slice(1).map((l) => {
      return { line: capitalize(l.join(' ')) };
    }),
  };
}

function remapQueue(
  queue: NonNullable<IEvent['queue']>,
  i18n: I18nContext,
  options?: { timezone?: string },
) {
  return {
    ...queue,
    departureTime: localeDate(queue.departureTime, options?.timezone),
    totalQueueTime: timeFormat(queue.totalQueueTime),
    ...separateQueueItems(queue.items, i18n),
  };
}

function separateQueueItems(queueItems: QueueItem[] | undefined, i18n: I18nContext) {
  const greetOrOrderItems: RemappedQueueItem[] = [];
  const otherItems: RemappedQueueItem[] = [];

  let detectItem = undefined;
  if (queueItems) {
    for (const item of queueItems) {
      if (item.type && ['Greet', 'Order'].includes(item.type)) {
        greetOrOrderItems.push(remapQueueItem(item, i18n));
      }
      if (!item.type || !['Detect', 'Greet', 'Order'].includes(item.type)) {
        otherItems.push(remapQueueItem(item, i18n));
      }
      if (item.type === 'Detect' && detectItem === undefined) {
        detectItem = remapQueueItem(item, i18n);
      }
    }
  }
  return { detectItem, greetOrOrderItems, otherItems };
}

function remapQueueItem(queueItem: QueueItem, i18n: I18nContext): RemappedQueueItem {
  return {
    ...queueItem,
    duration: timeFormat(queueItem.duration),
    isExit: queueItem.type === 'Exit',
    time: timestamp(queueItem.time),
    type: queueItem.type ? i18n['queue_items_type']?.values?.[queueItem.type] : undefined,
  };
}
